import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import clsx from "clsx";
import { useUI } from "@/store/ui";
import { formatCHF, formatRooms, formatSurface, relativeTime } from "@/lib/format";
import type { Listing } from "@/types";

export function ListingCard({ listing }: { listing: Listing }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: listing.id });
  const select = useUI((s) => s.select);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => select(listing.id)}
      className={clsx(
        "group cursor-pointer rounded-lg border border-ink-200 bg-white shadow-sm hover:shadow-md hover:border-ink-300 transition select-none overflow-hidden",
        isDragging && "opacity-60 shadow-lg",
      )}
    >
      {listing.coverImageUrl && (
        <div className="aspect-[4/3] bg-ink-100 overflow-hidden">
          <img
            src={listing.coverImageUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-3 space-y-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-semibold text-ink-800">
            {formatCHF(listing.priceCHF)}
          </span>
          <span className="text-xs text-ink-500 tabular-nums">
            {formatRooms(listing.rooms)} rm · {formatSurface(listing.surfaceM2)}
          </span>
        </div>
        <div className="text-xs text-ink-600 truncate">
          {listing.street ? `${listing.street}, ` : ""}
          {listing.city || "—"}
        </div>
        <div className="flex items-center justify-between text-[11px] text-ink-400">
          <span className="truncate">{listing.agencyName || listing.source}</span>
          <span className="tabular-nums">{relativeTime(listing.firstSeen)}</span>
        </div>
      </div>
    </article>
  );
}
